import dayjs from 'dayjs';

import {ELeadStage, EMessageSystemMessageType, type ILead, type IMessage} from 'src/services/api';
import {type IConversation} from 'src/api/landlord-messages-api';
import {DEFAULT_LEAD_NAME, ESmartNotificationType, type FinderCriteriaFn, type ISmartNotification} from './types';
import {type IShowing} from 'src/api/landlord-showings-api';

export const isLastMessageValidForSmartRepliesValid = (conversation: IConversation) => {
    // inbound and older than 7 days
    return conversation.last_message_direction === 'in' && conversation!.last_inbound_message_at && dayjs().diff(dayjs.utc(conversation.last_inbound_message_at), 'day', true) < 7;
};

const getStopMessageNotification = (lead: ILead, messages: IMessage[]): ISmartNotification | undefined => {
    const lastMessage = messages.filter(o => o.direction === 'in').toSorted((a, b) => dayjs(b.inserted_at).diff(dayjs(a.inserted_at)))[0];

    if (!lastMessage) {
        return;
    }

    const trimmedLower = lastMessage.contents.trim().toLowerCase();
    // e.g. "stop", "stop all", "stop messages", "please stop", "stop !!"
    const hasStopMessage = trimmedLower.includes('stop') &&trimmedLower.length < 15;
    // e.g. "i am no longer interested", "no longer interested in this property"
    const hasNoLongerInterestedMessage = trimmedLower.includes('no longer interested') && trimmedLower.length < 30;

    if (hasStopMessage ||hasNoLongerInterestedMessage) {
        return {
            title: '🛑 Stop Message Received',
            description: `${lead.name || DEFAULT_LEAD_NAME} has requested to stop receiving messages. Please respect their wishes and avoid further contact.`,
            type: ESmartNotificationType.warning,
            changeToSpecificStage: ELeadStage.NOT_INTERESTED
        };
    }
};

const getSystemMessageNotification = (lead: ILead, messages: IMessage[]): ISmartNotification | undefined => {
    const lastMessage = messages.toSorted((a, b) => dayjs(b.inserted_at).diff(dayjs(a.inserted_at)))[0];

    let changeToSpecificStage: ELeadStage | undefined;
    let title: string | undefined;
    let description: string | undefined;

    switch (lastMessage?.system_message_type) {
        case EMessageSystemMessageType.APPLICATION_RECEIVED:
            changeToSpecificStage = ELeadStage.APPLICATION_COMPLETED;
            title = '📋 Application Received';
            description = `Review ${lead.name || DEFAULT_LEAD_NAME}'s completed application and update lead stage.`;
            break;
        case EMessageSystemMessageType.APPLICATION_REQUESTED:
            changeToSpecificStage = ELeadStage.INVITED_TO_APPLY;
            title = '📝 Application Requested';
            description = `${lead.name || DEFAULT_LEAD_NAME} has been invited to apply. Update lead stage to track application progress.`;
            break;
        case EMessageSystemMessageType.TOUR_CANCELLED:
            changeToSpecificStage = ELeadStage.TOUR_REQUESTED;
            title = '❌ Tour Cancelled';
            description = `Tour with ${lead.name || DEFAULT_LEAD_NAME} was cancelled. Update lead stage to reschedule.`;
            break;
        case EMessageSystemMessageType.TOUR_CREATED:
        case EMessageSystemMessageType.TOUR_UPDATED:
            changeToSpecificStage = ELeadStage.TOUR_SCHEDULED;
            title = '📅 Tour Scheduled';
            description = `Tour has been scheduled with ${lead.name || DEFAULT_LEAD_NAME}. Update lead stage to track progress.`;
            break;
        case EMessageSystemMessageType.TOUR_REQUESTED:
            changeToSpecificStage = ELeadStage.TOUR_REQUESTED;
            title = '🔔 Tour Requested';
            description = `${lead.name || DEFAULT_LEAD_NAME} has requested a tour. Update lead stage to coordinate scheduling.`;
            break;
        default:
            return undefined;
    }

    if (changeToSpecificStage === lead.stage) {
        return undefined;
    }

    return {
        title,
        description,
        type: ESmartNotificationType.default,
        changeToSpecificStage,
    };
};

const STAGE_TO_NOTIFICATION_MAP: Partial<Record<ELeadStage, FinderCriteriaFn>> = {
    [ELeadStage.NOT_SET]: (lead) => {
        return {
            title: '❓ Lead Stage Not Set',
            description: `Set a stage for ${lead.name || DEFAULT_LEAD_NAME} to start tracking their progress.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.NEW]: (lead) => {
        return {
            title: '👋 New Lead Ready',
            description: `Begin the conversation with ${lead.name || DEFAULT_LEAD_NAME} and qualify their interest.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.CONTACTED]: (lead) => {
        return {
            title: '📱 Initial Contact Made',
            description: `Follow up with ${lead.name || DEFAULT_LEAD_NAME} to schedule a property tour.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.TOUR_REQUESTED]: (lead) => {
        return {
            title: '📅 Tour Request Pending',
            description: `Confirm tour details with ${lead.name || DEFAULT_LEAD_NAME} and set a specific time.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.TOUR_SCHEDULED]: (lead, messages, showings) => {
        const hasUpcomingShowing = showings.some(o => o.lead_uuid === lead.uuid && dayjs(o.start_time).isAfter(dayjs()));
        if (!hasUpcomingShowing) {
            return {
                title: '⚠️ No Upcoming Tour',
                description: `${lead.name || DEFAULT_LEAD_NAME} has a scheduled tour but no upcoming showing. Please confirm the tour details.`,
                type: ESmartNotificationType.default,
                changeToSpecificStage: ELeadStage.TOUR_COMPLETED,
            };
        }
        return {
            title: '🕒 Tour Coming Up',
            description: `Prepare for ${lead.name || DEFAULT_LEAD_NAME}'s upcoming property tour.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.TOUR_COMPLETED]: (lead) => {
        return {
            title: '✨ Tour Completed',
            description: `Follow up with ${lead.name || DEFAULT_LEAD_NAME} about their tour experience and next steps.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.INVITED_TO_APPLY]: (lead) => {
        return {
            title: '📝 Application Pending',
            description: `Monitor ${lead.name || DEFAULT_LEAD_NAME}'s application progress and assist if needed.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.APPLICATION_COMPLETED]: (lead) => {
        return {
            title: '📋 Application Ready',
            description: `You can move ${lead.name || DEFAULT_LEAD_NAME} to Application Approved if their application looks good.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.APPLICATION_APPROVED]: (lead) => {
        return {
            title: '✅ Ready for Lease',
            description: `Send lease documents to ${lead.name || DEFAULT_LEAD_NAME} for signature.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.LEASE_SIGNED]: (lead) => {
        return {
            title: '🔑 Move-in Ready',
            description: `Coordinate move-in details with ${lead.name || DEFAULT_LEAD_NAME}.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.NOT_INTERESTED]: (lead) => {
        return {
            title: '📌 Lead Inactive',
            description: `${lead.name || DEFAULT_LEAD_NAME} marked as not interested. Consider for future properties.`,
            type: ESmartNotificationType.default,
        };
    },
    [ELeadStage.NOT_QUALIFIED]: (lead) => {
        return {
            title: '⚠️ Not Qualified',
            description: `${lead.name || DEFAULT_LEAD_NAME} does not meet the requirements for this property. Consider suggesting other properties that might be a better fit.`,
            type: ESmartNotificationType.default,
        };
    }
};


export const getSmartNotificationForLead = (lead: ILead, messages: IMessage[], showings: IShowing[]): ISmartNotification | undefined => {
    const stopMessageNotification = getStopMessageNotification(lead, messages);
    if (stopMessageNotification) {
        return stopMessageNotification;
    }

    const systemMessageNotification = getSystemMessageNotification(lead, messages);

    if (systemMessageNotification) {
        return systemMessageNotification;
    }

    const fn = STAGE_TO_NOTIFICATION_MAP[lead.stage];
    if (!fn) {
        return;
    }
    return fn(lead, messages, showings);
};
